// The one home of control-column geometry. A cell that holds an interactive control fits the control's box
// plus the focus ring's space on every side inside its own content box, so no ring or hover fill reaches a
// neighbour. A control column's width is derived here from the control's Buttons size tier, the ring space
// and the cell's horizontal padding; the stylesheets set the same padding (geometry tests read them back).

import {
  DEFAULT_LABELLED_ACTIONS_WIDTH,
  DEFAULT_WIDTHS,
} from '../config/defaults'

/** Buttons' focus ring outside a control's box: a 2px outline at a 2px offset (z5-me-components.css). */
export const RING_SPACE = 4

/** Buttons icon-button sizes in CSS pixels, by tier (.me-btn-icon, -lg, -sm, -xs in z5-me-components.css). */
export const ICON_SIZES = { lg: 38, md: 34, sm: 30, xs: 24 }

/** The site's native checkbox, unrestyled. */
export const CHECKBOX_SIZE = 13

/** The size tier each icon control renders at, by density. */
export const CONTROL_TIERS = {
  expand: { comfortable: 'sm', compact: 'sm' },
  actions: { comfortable: 'sm', compact: 'sm' },
}

/**
 * Each control column's horizontal cell padding, start and end. Every side holds at least RING_SPACE; the
 * rest is the column's inset. z5-me-table.css (expand, actions) and z6-me-table-selection.css (select) set
 * these values.
 */
export const CONTROL_PADDING = {
  select: { start: 14, end: RING_SPACE },
  expand: { start: 8, end: RING_SPACE },
  actions: { start: 12, end: 8 },
}

/**
 * The design's room for a column beyond its control, when larger than the derived floor: the selection
 * column's 44px, the kebab's 64px, and a labelled trigger, whose width follows its label (it truncates in
 * its cell rather than overflowing it).
 */
const RESERVED = {
  select: 44,
  actions: DEFAULT_WIDTHS.actions,
  labelledActions: DEFAULT_LABELLED_ACTIONS_WIDTH,
}

/** The tier a control renders at in a density. */
export const tierOf = (control, density) =>
  (CONTROL_TIERS[control] || {})[density] ||
  (CONTROL_TIERS[control] || {}).comfortable ||
  'sm'

/** The control's box width in CSS pixels; a labelled trigger has no fixed width and counts as 0. */
export const controlSize = (column, density, { labelled = false } = {}) => {
  if (column === 'select') return CHECKBOX_SIZE
  if (column === 'actions' && labelled) return 0
  return ICON_SIZES[tierOf(column, density)]
}

/** The narrowest a control column may be: its control, plus cell padding that holds the ring on both sides. */
export const controlFloor = (column, density, options) => {
  const pad = CONTROL_PADDING[column]
  return (
    controlSize(column, density, options) +
    Math.max(pad.start, RING_SPACE) +
    Math.max(pad.end, RING_SPACE)
  )
}

/**
 * A control column's width: its derived floor, or the design's room when that is larger.
 * @param {'select'|'expand'|'actions'} column
 * @param {import('../types').Density} density
 * @param {{labelled?: boolean}} [options] a labelled row-actions trigger in place of the kebab
 * @returns {number}
 */
export const controlWidth = (column, density, options = {}) => {
  const reserved =
    column === 'actions' && options.labelled
      ? RESERVED.labelledActions
      : RESERVED[column] || 0
  return Math.max(controlFloor(column, density, options), reserved)
}
